import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  Download,
  FileDown,
  FileText,
  NotebookPen,
  Paperclip,
  Pin,
  PinOff,
  Plus,
  RotateCcw,
  Search,
  Share2,
  Tag,
  Trash2,
  UserRound,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import RichTextEditor from "../components/tasks/RichTextEditor";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const yest = new Date(now);
  yest.setDate(now.getDate() - 1);
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
  }
  if (d.toDateString() === yest.toDateString()) return "Dün";
  return d.toLocaleDateString("tr-TR");
};

const fmtSize = (b) =>
  b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;

const sortNotes = (list) =>
  [...list].sort(
    (a, b) =>
      (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0) ||
      new Date(b.updated_at || 0) - new Date(a.updated_at || 0),
  );

/** Görsel ekleri yetkili istekle indirip küçük önizleme gösterir. */
function NoteImage({ att, onDelete, canDelete = true }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    let obj = null;
    api
      .get(`/attachments/${att.id}/download`, { responseType: "blob" })
      .then((res) => {
        obj = URL.createObjectURL(res.data);
        if (alive) setUrl(obj);
      })
      .catch(() => {});
    return () => {
      alive = false;
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [att.id]);
  return (
    <div className="group relative h-28 w-28 overflow-hidden rounded-lg border border-zinc-200 bg-zinc-50">
      {url ? (
        <img
          src={url}
          alt={att.original_filename}
          title={att.original_filename}
          className="h-full w-full cursor-zoom-in object-cover"
          onClick={() => window.open(url, "_blank")}
        />
      ) : (
        <div className="flex h-full items-center justify-center text-[10px] text-zinc-400">…</div>
      )}
      {canDelete && (
      <button
        type="button"
        onClick={onDelete}
        data-testid={`note-att-delete-${att.id}`}
        className="absolute right-1 top-1 hidden rounded-full bg-black/60 p-1 text-white group-hover:block"
      >
        <X className="h-3 w-3" />
      </button>
      )}
    </div>
  );
}

export default function Notes() {
  const [notes, setNotes] = useState([]);
  const [q, setQ] = useState("");
  const [activeTag, setActiveTag] = useState(null);
  const [note, setNote] = useState(null);
  const [saveState, setSaveState] = useState("idle");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [view, setView] = useState("notes");
  const [trash, setTrash] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [shareIds, setShareIds] = useState([]);
  const [shareSearch, setShareSearch] = useState("");
  const [exporting, setExporting] = useState(false);
  const readOnly = !!note?.is_shared;
  const noteRef = useRef(null);
  const saveTimer = useRef(null);
  const pendingRef = useRef(false);
  const titleRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    noteRef.current = note;
  }, [note]);

  const load = useCallback(async () => {
    const { data } = await api.get("/notes");
    setNotes(data || []);
  }, []);

  useEffect(() => {
    load();
    return () => flushSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const savePayload = (cur) => ({
    title: cur.title || "",
    content: cur.content || "",
    tags: cur.tags || [],
  });

  const flushSave = async () => {
    if (!pendingRef.current) return;
    clearTimeout(saveTimer.current);
    pendingRef.current = false;
    const cur = noteRef.current;
    if (cur) {
      try {
        await api.patch(`/notes/${cur.id}`, savePayload(cur));
      } catch {
        /* sessiz */
      }
    }
  };

  const scheduleSave = (patch) => {
    setNote((prev) => (prev ? { ...prev, ...patch } : prev));
    setNotes((prev) =>
      sortNotes(
        prev.map((n) =>
          n.id === noteRef.current?.id
            ? {
                ...n,
                ...(patch.title !== undefined ? { title: patch.title } : {}),
                ...(patch.tags !== undefined ? { tags: patch.tags } : {}),
                updated_at: new Date().toISOString(),
              }
            : n,
        ),
      ),
    );
    setSaveState("saving");
    pendingRef.current = true;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      pendingRef.current = false;
      const cur = noteRef.current;
      if (!cur) return;
      try {
        await api.patch(`/notes/${cur.id}`, savePayload(cur));
        setSaveState("saved");
      } catch {
        setSaveState("idle");
        toast.error("Not kaydedilemedi");
      }
    }, 800);
  };

  const openNote = async (id) => {
    await flushSave();
    try {
      const { data } = await api.get(`/notes/${id}`);
      setNote(data);
      setSaveState("idle");
      setTagInput("");
    } catch {
      toast.error("Not açılamadı");
    }
  };

  const createNote = async () => {
    await flushSave();
    const { data } = await api.post("/notes", { title: "", content: "" });
    setNotes((prev) => sortNotes([{ ...data, snippet: "" }, ...prev]));
    setNote({ ...data, attachments: [] });
    setSaveState("idle");
    setTimeout(() => titleRef.current?.focus(), 60);
  };

  const togglePin = async () => {
    const cur = noteRef.current;
    if (!cur) return;
    const next = !cur.pinned;
    setNote((prev) => ({ ...prev, pinned: next }));
    setNotes((prev) =>
      sortNotes(prev.map((n) => (n.id === cur.id ? { ...n, pinned: next } : n))),
    );
    try {
      await api.patch(`/notes/${cur.id}`, { pinned: next });
      toast.success(next ? "Not sabitlendi 📌" : "Sabitleme kaldırıldı");
    } catch {
      toast.error("İşlem başarısız");
    }
  };

  const exportPdf = async () => {
    const cur = noteRef.current;
    if (!cur || exporting) return;
    setExporting(true);
    try {
      const res = await api.get(`/notes/${cur.id}/export.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(cur.title || "").trim() || "not"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Not PDF olarak indirildi 📄");
    } catch {
      toast.error("PDF oluşturulamadı");
    } finally {
      setExporting(false);
    }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    const cur = noteRef.current;
    const tags = cur?.tags || [];
    if (tags.some((x) => x.toLowerCase() === t.toLowerCase())) {
      setTagInput("");
      return;
    }
    setTagInput("");
    scheduleSave({ tags: [...tags, t] });
  };

  const removeTag = (t) => {
    const tags = (noteRef.current?.tags || []).filter((x) => x !== t);
    scheduleSave({ tags });
  };

  const uploadFiles = async (files) => {
    const cur = noteRef.current;
    if (!cur || !files?.length) return;
    setUploading(true);
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post(`/notes/${cur.id}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setNote((prev) =>
          prev ? { ...prev, attachments: [...(prev.attachments || []), data] } : prev,
        );
      } catch (err) {
        toast.error(
          err.response?.status === 415
            ? `Desteklenmeyen dosya türü: ${file.name}`
            : err.response?.status === 413
              ? `Dosya çok büyük (max 10 MB): ${file.name}`
              : `Yüklenemedi: ${file.name}`,
        );
      }
    }
    setUploading(false);
  };

  const deleteAttachment = async (att) => {
    const cur = noteRef.current;
    try {
      await api.delete(`/notes/${cur.id}/attachments/${att.id}`);
      setNote((prev) =>
        prev
          ? { ...prev, attachments: (prev.attachments || []).filter((a) => a.id !== att.id) }
          : prev,
      );
    } catch {
      toast.error("Ek silinemedi");
    }
  };

  const downloadAttachment = async (att) => {
    try {
      const res = await api.get(`/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Dosya indirilemedi");
    }
  };

  const deleteNote = async () => {
    const cur = noteRef.current;
    if (!cur) return;
    pendingRef.current = false;
    clearTimeout(saveTimer.current);
    try {
      await api.delete(`/notes/${cur.id}`);
      setNotes((prev) => prev.filter((n) => n.id !== cur.id));
      setNote(null);
      setDeleteOpen(false);
      toast.success("Not çöp kutusuna taşındı — 30 gün içinde geri alabilirsin");
    } catch {
      toast.error("Not silinemedi");
    }
  };

  const openTrash = async () => {
    await flushSave();
    try {
      const { data } = await api.get("/notes/trash");
      setTrash(data || []);
      setView("trash");
      setNote(null);
    } catch {
      toast.error("Çöp kutusu açılamadı");
    }
  };

  const restoreNote = async (n) => {
    try {
      await api.post(`/notes/${n.id}/restore`);
      setTrash((prev) => prev.filter((x) => x.id !== n.id));
      await load();
      toast.success(`"${n.title || "Adsız not"}" geri alındı`);
    } catch {
      toast.error("Geri alınamadı");
    }
  };

  const permanentDelete = async (n) => {
    try {
      await api.delete(`/notes/${n.id}/permanent`);
      setTrash((prev) => prev.filter((x) => x.id !== n.id));
      toast.success("Not kalıcı olarak silindi");
    } catch {
      toast.error("Silinemedi");
    }
  };

  const openShare = async () => {
    const cur = noteRef.current;
    if (!cur) return;
    setShareIds(cur.shared_with || []);
    setShareSearch("");
    setShareOpen(true);
    if (members.length === 0) {
      try {
        const { data } = await api.get("/users", { params: { assignable: true } });
        setMembers(data || []);
      } catch {
        /* sessiz */
      }
    }
  };

  const saveShare = async () => {
    const cur = noteRef.current;
    try {
      const { data } = await api.put(`/notes/${cur.id}/share`, { user_ids: shareIds });
      setNote((prev) => (prev ? { ...prev, shared_with: data.shared_with } : prev));
      setNotes((prev) =>
        prev.map((n) => (n.id === cur.id ? { ...n, shared_with: data.shared_with } : n)),
      );
      setShareOpen(false);
      toast.success(
        data.shared_with.length
          ? `Not ${data.shared_with.length} kişiyle paylaşıldı`
          : "Paylaşım kaldırıldı",
      );
    } catch {
      toast.error("Paylaşım kaydedilemedi");
    }
  };

  const allTags = [...new Set(notes.flatMap((n) => n.tags || []))].sort((a, b) =>
    a.localeCompare(b, "tr"),
  );
  const query = q.trim().toLocaleLowerCase("tr");
  const filtered = notes.filter((n) => {
    if (activeTag && !(n.tags || []).includes(activeTag)) return false;
    if (!query) return true;
    return (
      (n.title || "").toLocaleLowerCase("tr").includes(query) ||
      (n.snippet || "").toLocaleLowerCase("tr").includes(query) ||
      (n.tags || []).some((t) => t.toLocaleLowerCase("tr").includes(query))
    );
  });

  const images = (note?.attachments || []).filter((a) =>
    (a.content_type || "").startsWith("image/"),
  );
  const files = (note?.attachments || []).filter(
    (a) => !(a.content_type || "").startsWith("image/"),
  );

  return (
    <div className="flex h-[calc(100vh-120px)] gap-0 p-2 sm:p-6" data-testid="notes-page">
      {/* Sol: not listesi */}
      <div
        className={[
          "w-full flex-col rounded-xl border border-zinc-200 bg-white md:flex md:max-w-xs md:flex-none md:rounded-l-xl md:rounded-r-none",
          note ? "hidden" : "flex",
        ].join(" ")}
      >
        <div className="space-y-2 border-b border-zinc-100 p-3">
          <div className="flex items-center justify-between">
            <h1 className="font-display text-lg font-semibold text-zinc-950">
              {view === "trash" ? "Çöp Kutusu" : "Notlar"}
            </h1>
            <span className="text-xs text-zinc-400">
              {view === "trash" ? trash.length : notes.length}
            </span>
          </div>
          {view === "trash" ? (
            <Button
              onClick={() => setView("notes")}
              variant="outline"
              data-testid="back-to-notes-btn"
              className="w-full gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Notlara dön
            </Button>
          ) : (
            <>
          <Button
            onClick={createNote}
            data-testid="new-note-btn"
            className="w-full gap-2 bg-emerald-600 text-white hover:bg-emerald-700"
          >
            <Plus className="h-4 w-4" />
            Yeni Not
          </Button>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Notlarda ara..."
              data-testid="notes-search-input"
              className="h-8 pl-8 text-sm"
            />
          </div>
            </>
          )}
          {view === "notes" && allTags.length > 0 && (
            <div className="flex flex-wrap gap-1" data-testid="notes-tag-filter">
              <button
                type="button"
                onClick={() => setActiveTag(null)}
                data-testid="notes-tag-all"
                className={[
                  "rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors",
                  !activeTag
                    ? "bg-zinc-900 text-white"
                    : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200",
                ].join(" ")}
              >
                Tümü
              </button>
              {allTags.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setActiveTag(activeTag === t ? null : t)}
                  data-testid={`notes-tag-filter-${t}`}
                  className={[
                    "rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors",
                    activeTag === t
                      ? "bg-emerald-600 text-white"
                      : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
                  ].join(" ")}
                >
                  #{t}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {view === "trash" ? (
            <>
              {trash.length === 0 && (
                <p className="p-4 text-center text-xs text-zinc-400">Çöp kutusu boş.</p>
              )}
              {trash.map((n) => (
                <div
                  key={n.id}
                  data-testid={`trash-item-${n.id}`}
                  className="border-b border-zinc-100 px-3 py-2.5"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span
                      className={[
                        "min-w-0 flex-1 truncate text-sm font-medium",
                        n.title ? "text-zinc-900" : "italic text-zinc-400",
                      ].join(" ")}
                    >
                      {n.title || "Adsız not"}
                    </span>
                    <span className="shrink-0 text-[10px] font-medium text-red-500">
                      {n.days_left} gün kaldı
                    </span>
                  </div>
                  {n.snippet && (
                    <p className="mt-0.5 truncate text-xs text-zinc-500">{n.snippet}</p>
                  )}
                  <div className="mt-1.5 flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => restoreNote(n)}
                      data-testid={`trash-restore-${n.id}`}
                      className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700 hover:bg-emerald-100"
                    >
                      <RotateCcw className="h-3 w-3" />
                      Geri al
                    </button>
                    <button
                      type="button"
                      onClick={() => permanentDelete(n)}
                      data-testid={`trash-purge-${n.id}`}
                      className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2 py-1 text-[11px] font-medium text-red-600 hover:bg-red-100"
                    >
                      <Trash2 className="h-3 w-3" />
                      Kalıcı sil
                    </button>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <>
          {filtered.length === 0 && (
            <p className="p-4 text-center text-xs text-zinc-400">
              {notes.length === 0 ? "Henüz not yok — Yeni Not ile başla." : "Eşleşen not yok."}
            </p>
          )}
          {filtered.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => openNote(n.id)}
              data-testid={`note-item-${n.id}`}
              className={[
                "block w-full border-b border-zinc-100 px-3 py-2.5 text-left transition-colors",
                note?.id === n.id ? "bg-emerald-50/70" : "hover:bg-zinc-50",
              ].join(" ")}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="flex min-w-0 flex-1 items-center gap-1">
                  {n.pinned && (
                    <Pin
                      data-testid={`note-pinned-${n.id}`}
                      className="h-3 w-3 shrink-0 fill-amber-400 text-amber-500"
                    />
                  )}
                  <span
                    className={[
                      "truncate text-sm font-medium",
                      n.title ? "text-zinc-900" : "italic text-zinc-400",
                    ].join(" ")}
                  >
                    {n.title || "Adsız not"}
                  </span>
                  {!n.is_shared && (n.shared_with || []).length > 0 && (
                    <span
                      data-testid={`note-shared-count-${n.id}`}
                      className="inline-flex shrink-0 items-center gap-0.5 text-[10px] text-sky-600"
                      title={`${n.shared_with.length} kişiyle paylaşıldı`}
                    >
                      <Share2 className="h-3 w-3" />
                      {n.shared_with.length}
                    </span>
                  )}
                </span>
                <span className="shrink-0 text-[10px] text-zinc-400">
                  {fmtWhen(n.updated_at)}
                </span>
              </div>
              {n.is_shared && (
                <span
                  data-testid={`note-shared-by-${n.id}`}
                  className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-sky-50 px-1.5 py-px text-[10px] font-medium text-sky-700"
                >
                  <UserRound className="h-3 w-3" />
                  {n.owner_name || "Ekipten"}
                </span>
              )}
              {n.snippet && (
                <p className="mt-0.5 truncate text-xs text-zinc-500">{n.snippet}</p>
              )}
              {(n.tags || []).length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {n.tags.slice(0, 3).map((t) => (
                    <span
                      key={t}
                      className="rounded-full bg-emerald-50 px-1.5 py-px text-[10px] font-medium text-emerald-700"
                    >
                      #{t}
                    </span>
                  ))}
                  {n.tags.length > 3 && (
                    <span className="text-[10px] text-zinc-400">+{n.tags.length - 3}</span>
                  )}
                </div>
              )}
            </button>
          ))}
            </>
          )}
        </div>
        {view === "notes" && (
          <button
            type="button"
            onClick={openTrash}
            data-testid="open-trash-btn"
            className="flex items-center justify-center gap-1.5 rounded-bl-xl border-t border-zinc-100 py-2 text-xs text-zinc-500 transition-colors hover:bg-zinc-50 hover:text-zinc-700"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Çöp Kutusu
          </button>
        )}
      </div>

      {/* Sağ: editör */}
      <div
        className={[
          "relative min-w-0 flex-1 flex-col rounded-xl border bg-white md:rounded-l-none md:rounded-r-xl md:border-l-0",
          note ? "flex" : "hidden md:flex",
          dragActive ? "border-emerald-400 ring-2 ring-emerald-200" : "border-zinc-200",
        ].join(" ")}
        onDragOver={(e) => {
          if (!note || readOnly) return;
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e) => {
          if (!note || readOnly) return;
          e.preventDefault();
          setDragActive(false);
          uploadFiles(e.dataTransfer.files);
        }}
      >
        {dragActive && note && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-r-xl bg-emerald-50/80">
            <p className="text-sm font-semibold text-emerald-700">
              Dosyaları buraya bırak — nota eklenecek
            </p>
          </div>
        )}
        {!note ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 text-zinc-400">
            <NotebookPen className="h-10 w-10" strokeWidth={1.25} />
            <p className="text-sm">Bir not seç veya yeni not oluştur</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2 border-b border-zinc-100 px-2 py-2 sm:gap-3 sm:px-4">
              <div className="flex min-w-0 items-center gap-1.5">
                <button
                  type="button"
                  onClick={async () => {
                    await flushSave();
                    setNote(null);
                  }}
                  data-testid="notes-back-to-list-btn"
                  className="shrink-0 rounded-md p-1.5 text-zinc-500 hover:bg-zinc-100 md:hidden"
                  aria-label="Listeye dön"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <span className="truncate text-[11px] text-zinc-400" data-testid="note-save-state">
                  {readOnly
                    ? `Paylaşan: ${note.owner_name || "Ekipten"} · salt okunur`
                    : uploading
                      ? "Dosya yükleniyor…"
                      : saveState === "saving"
                        ? "Kaydediliyor…"
                        : saveState === "saved"
                          ? "Kaydedildi ✓"
                          : fmtWhen(note.updated_at) && `Son düzenleme: ${fmtWhen(note.updated_at)}`}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-0.5 sm:gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={exportPdf}
                  disabled={exporting}
                  data-testid="export-note-pdf-btn"
                  className="gap-1.5 text-zinc-500 hover:bg-zinc-50"
                >
                  <FileDown className="h-4 w-4" />
                  <span className="hidden sm:inline">{exporting ? "Hazırlanıyor…" : "PDF"}</span>
                </Button>
                {!readOnly && (
                <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={openShare}
                  data-testid="share-note-btn"
                  className="gap-1.5 text-sky-600 hover:bg-sky-50"
                >
                  <Share2 className="h-4 w-4" />
                  <span className="hidden sm:inline">Paylaş</span>
                  {(note.shared_with || []).length > 0 && (
                    <span className="rounded-full bg-sky-100 px-1.5 text-[10px] font-semibold text-sky-700">
                      {note.shared_with.length}
                    </span>
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={togglePin}
                  data-testid="pin-note-btn"
                  className={
                    note.pinned
                      ? "gap-1.5 text-amber-600 hover:bg-amber-50"
                      : "gap-1.5 text-zinc-500 hover:bg-zinc-50"
                  }
                >
                  {note.pinned ? (
                    <>
                      <PinOff className="h-4 w-4" />
                      <span className="hidden sm:inline">Sabitlemeyi kaldır</span>
                    </>
                  ) : (
                    <>
                      <Pin className="h-4 w-4" />
                      <span className="hidden sm:inline">Sabitle</span>
                    </>
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                  data-testid="attach-note-btn"
                  className="gap-1.5 text-zinc-500 hover:bg-zinc-50"
                >
                  <Paperclip className="h-4 w-4" />
                  <span className="hidden sm:inline">Dosya ekle</span>
                </Button>
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  className="hidden"
                  data-testid="note-file-input"
                  onChange={(e) => {
                    uploadFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDeleteOpen(true)}
                  data-testid="delete-note-btn"
                  className="gap-1.5 text-red-600 hover:bg-red-50 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="hidden sm:inline">Sil</span>
                </Button>
                </>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <input
                ref={titleRef}
                value={note.title || ""}
                onChange={(e) => scheduleSave({ title: e.target.value })}
                placeholder="Başlık"
                disabled={readOnly}
                data-testid="note-title-input"
                className="w-full rounded-md border-0 bg-transparent font-display text-2xl font-semibold text-zinc-950 outline-none ring-offset-2 focus-visible:ring-1 focus-visible:ring-zinc-200 placeholder:text-zinc-300 disabled:opacity-90"
              />
              {/* Etiketler */}
              <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="note-tags">
                <Tag className="h-3.5 w-3.5 text-zinc-400" />
                {(note.tags || []).map((t) => (
                  <span
                    key={t}
                    data-testid={`note-tag-${t}`}
                    className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700"
                  >
                    #{t}
                    {!readOnly && (
                    <button
                      type="button"
                      onClick={() => removeTag(t)}
                      data-testid={`note-tag-remove-${t}`}
                      className="text-emerald-500 hover:text-emerald-800"
                    >
                      <X className="h-3 w-3" />
                    </button>
                    )}
                  </span>
                ))}
                {!readOnly && (
                <input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  onBlur={addTag}
                  placeholder="Etiket ekle…"
                  data-testid="note-tag-input"
                  className="w-24 rounded-md border-0 bg-transparent text-xs text-zinc-600 outline-none ring-offset-2 focus-visible:ring-1 focus-visible:ring-zinc-200 placeholder:text-zinc-300"
                />
                )}
              </div>
              <div className="mt-3">
                <RichTextEditor
                  key={note.id}
                  value={note.content || ""}
                  onChange={(md) => scheduleSave({ content: md })}
                  placeholder="Yazmaya başla… (görselleri buraya sürükleyebilirsin)"
                  rows={14}
                  readOnly={readOnly}
                  data-testid="note-content-editor"
                />
              </div>
              {/* Ekler */}
              {(images.length > 0 || files.length > 0) && (
                <div className="mt-4 space-y-3" data-testid="note-attachments">
                  {images.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {images.map((att) => (
                        <NoteImage
                          key={att.id}
                          att={att}
                          canDelete={!readOnly}
                          onDelete={() => deleteAttachment(att)}
                        />
                      ))}
                    </div>
                  )}
                  {files.map((att) => (
                    <div
                      key={att.id}
                      data-testid={`note-file-${att.id}`}
                      className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50/60 px-3 py-2"
                    >
                      <FileText className="h-4 w-4 shrink-0 text-zinc-400" />
                      <span className="min-w-0 flex-1 truncate text-sm text-zinc-700">
                        {att.original_filename}
                      </span>
                      <span className="shrink-0 text-[11px] text-zinc-400">{fmtSize(att.size)}</span>
                      <button
                        type="button"
                        onClick={() => downloadAttachment(att)}
                        data-testid={`note-file-download-${att.id}`}
                        className="rounded p-1 text-zinc-500 hover:bg-zinc-100"
                      >
                        <Download className="h-3.5 w-3.5" />
                      </button>
                      {!readOnly && (
                      <button
                        type="button"
                        onClick={() => deleteAttachment(att)}
                        data-testid={`note-file-delete-${att.id}`}
                        className="rounded p-1 text-red-500 hover:bg-red-50"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="note-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Notu sil</AlertDialogTitle>
            <AlertDialogDescription>
              “{note?.title || "Adsız not"}” ve ekleri kalıcı olarak silinecek. Emin misin?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="note-delete-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteNote}
              data-testid="note-delete-confirm"
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
